/* MembersList — admin/recruiter view of account members with selection + per-row actions. */
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

export function MembersList({
  members, myUserId, isAdmin, selected, setSelected,
  onModifyRole, onSuspend, onRevoke, onBulkRevoke, onBulkRoleDemote, bulkLimits,
}) {
  const toggle = (uid) => {
    const n = new Set(selected);
    if (n.has(uid)) n.delete(uid); else n.add(uid);
    setSelected(n);
  };

  const selectedArr = Array.from(selected);

  if (!members.length) {
    return (
      <Card className="p-8 text-center text-sm text-slate-300" data-testid="emp-members-list-empty">
        No members yet. Invite your first teammate.
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="emp-members-list">
      {isAdmin && selectedArr.length > 0 && (
        <Card className="flex flex-wrap items-center gap-3 border-amber-400/30 bg-amber-500/10/50 p-3 dark:bg-amber-950/20"
          data-testid="emp-members-bulk-bar">
          <span className="text-sm font-medium" data-testid="emp-members-bulk-count">
            {selectedArr.length} selected
          </span>
          <span className="text-xs text-slate-300">
            (cap {bulkLimits.revoke} revoke / {bulkLimits.roleModify} role)
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => onBulkRoleDemote('reviewer')}
              data-testid="emp-members-bulk-demote-reviewer">→ reviewer</Button>
            <Button size="sm" variant="outline" onClick={() => onBulkRoleDemote('recruiter')}
              data-testid="emp-members-bulk-demote-recruiter">→ recruiter</Button>
            <Button size="sm" variant="destructive" onClick={onBulkRevoke}
              data-testid="emp-members-bulk-revoke">Bulk revoke</Button>
          </div>
        </Card>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-300">
            <tr>
              {isAdmin && <th className="w-8 p-2"></th>}
              <th className="p-2">User</th>
              <th className="p-2">Role</th>
              <th className="p-2">Joined</th>
              {isAdmin && <th className="p-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const isSelf = m.user_id === myUserId;
              const canAct = isAdmin && !isSelf;
              return (
                <tr key={m.user_id} className="border-t border-zinc-200 dark:border-zinc-800"
                  data-testid={`emp-members-row-${m.user_id}`}>
                  {isAdmin && (
                    <td className="p-2">
                      <Checkbox
                        checked={selected.has(m.user_id)}
                        onCheckedChange={() => toggle(m.user_id)}
                        disabled={isSelf}
                        aria-label={`select member ${m.user_id}`}
                        data-testid={`emp-members-select-${m.user_id}`}
                      />
                    </td>
                  )}
                  <td className="p-2 font-mono text-xs">
                    {m.user_id.slice(0, 8)}…
                    {isSelf && <Badge variant="secondary" className="ml-2">you</Badge>}
                  </td>
                  <td className="p-2">
                    <Badge variant={m.role === 'admin' ? 'default' : 'outline'}
                      data-testid={`emp-members-role-${m.user_id}`}>
                      {m.role}
                    </Badge>
                  </td>
                  <td className="p-2 text-xs text-slate-300">
                    {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '—'}
                  </td>
                  {isAdmin && (
                    <td className="p-2">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" disabled={!canAct}
                          onClick={() => onModifyRole(m)}
                          data-testid={`emp-members-modify-role-${m.user_id}`}>
                          Role
                        </Button>
                        <Button size="sm" variant="ghost" disabled={!canAct}
                          onClick={() => onSuspend(m)}
                          data-testid={`emp-members-suspend-${m.user_id}`}>
                          Suspend
                        </Button>
                        <Button size="sm" variant="ghost" disabled={!canAct}
                          onClick={() => onRevoke(m.user_id)}
                          className="text-rose-300 hover:text-rose-200"
                          data-testid={`emp-members-revoke-${m.user_id}`}>
                          Revoke
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
